/**
 * Bottle service for CRUD operations
 */

import { supabase } from './supabase';
import { imageService } from './imageService';

/**
 * Checks whether another bottle (e.g. a clone sharing the same image) still
 * references the given image URL, so a storage delete doesn't break it.
 * @param {string} imageUrl - Image URL to check
 * @param {string} excludeBottleId - Bottle UUID to exclude from the check
 * @returns {Promise<boolean>}
 */
async function isImageReferencedElsewhere(imageUrl, excludeBottleId) {
  const { data, error } = await supabase
    .from('bottles')
    .select('id')
    .neq('id', excludeBottleId)
    .or(`image_front_url.eq.${imageUrl},image_back_url.eq.${imageUrl}`)
    .limit(1);

  if (error) throw error;
  return data.length > 0;
}

/**
 * Bottle service object with all database operations
 */
export const bottleService = {
  /**
   * Fetches bottles with pagination, sorting, and filtering
   * @param {Object} options - Query options
   * @returns {Promise<{data: Array, count: number}>} Bottles and total count
   */
  async getBottles({ page = 1, limit = 20, sortBy = 'created_at', sortOrder = 'desc', filters = {} }) {
    const { data: { user } } = await supabase.auth.getUser();

    // Filtering by distillery country/state requires an inner join so the
    // filter also restricts the top-level bottle rows, not just the embedded resource.
    const needsDistilleryInnerJoin = Boolean(filters.country || filters.state);
    const distillerySelect = needsDistilleryInnerJoin
      ? 'distilleries!inner(id, distillery_name, country, state_region)'
      : 'distilleries(id, distillery_name, country)';

    let query = supabase
      .from('bottles')
      .select(`*, ${distillerySelect}`, { count: 'exact' })
      .eq('user_id', user.id);

    // Special handling for purchase_date: nulls treated as oldest, with secondary sort by name
    if (sortBy === 'purchase_date') {
      const isAscending = sortOrder === 'asc';
      // nullsFirst when ascending (oldest first shows nulls at top)
      // nullsFirst false when descending (newest first shows nulls at bottom)
      query = query
        .order('purchase_date', { ascending: isAscending, nullsFirst: isAscending })
        .order('bottle_name', { ascending: true });
    } else {
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });
    }

    if (filters.search) {
      query = query.or(`bottle_name.ilike.%${filters.search}%,notes.ilike.%${filters.search}%,tasting_notes.ilike.%${filters.search}%`);
    }

    if (filters.type) {
      query = query.eq('type', filters.type);
    }

    if (filters.status) {
      if (Array.isArray(filters.status) && filters.status.length > 0) {
        query = query.in('status', filters.status);
      } else if (typeof filters.status === 'string') {
        query = query.eq('status', filters.status);
      }
    }

    if (filters.distillery_id) {
      query = query.eq('distillery_id', filters.distillery_id);
    }

    if (filters.size) {
      query = query.eq('size', filters.size);
    }

    if (filters.store_pick) {
      query = query.eq('store_pick', filters.store_pick);
    }

    if (filters.country) {
      query = query.eq('distilleries.country', filters.country);
    }

    if (filters.state) {
      query = query.eq('distilleries.state_region', filters.state);
    }

    if (filters.price_min != null) {
      query = query.gte('purchase_price', filters.price_min);
    }

    if (filters.price_max != null) {
      query = query.lt('purchase_price', filters.price_max);
    }

    if (limit !== 'All') {
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);
    }

    const { data, error, count } = await query;

    if (error) throw error;
    return { data: data || [], count: count || 0 };
  },

  /**
   * Fetches the distinct store pick values used across the user's bottles
   * @returns {Promise<Array<string>>} Sorted list of distinct store names
   */
  async getStorePicks() {
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('bottles')
      .select('store_pick')
      .eq('user_id', user.id)
      .not('store_pick', 'is', null)
      .neq('store_pick', '');

    if (error) throw error;

    const unique = [...new Set((data || []).map(row => row.store_pick))];
    return unique.sort((a, b) => a.localeCompare(b));
  },

  /**
   * Fetches a single bottle by ID
   * @param {string} id - Bottle UUID
   * @returns {Promise<Object>} Bottle data with distillery
   */
  async getBottleById(id) {
    const { data, error } = await supabase
      .from('bottles')
      .select('*, distilleries(*)')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Creates a new bottle
   * @param {Object} bottle - Bottle data (may include pendingImages, removedImages, existingImages)
   * @returns {Promise<Object>} Created bottle
   */
  async createBottle(bottle) {
    const { data: { user } } = await supabase.auth.getUser();

    // Extract image-related fields
    const { pendingImages, removedImages, existingImages, ...bottleData } = bottle;

    // Insert the bottle first
    const { data, error } = await supabase
      .from('bottles')
      .insert({
        ...bottleData,
        user_id: user.id,
        created_by: user.id
      })
      .select('*, distilleries(id, distillery_name, country)')
      .single();

    if (error) throw error;

    // Upload any pending images
    if (pendingImages) {
      const imageUpdates = {};

      if (pendingImages.front) {
        const frontUrl = await imageService.uploadBottleImage(pendingImages.front, data.id, 'front');
        imageUpdates.image_front_url = frontUrl;
      }

      if (pendingImages.back) {
        const backUrl = await imageService.uploadBottleImage(pendingImages.back, data.id, 'back');
        imageUpdates.image_back_url = backUrl;
      }

      // Update the bottle with image URLs if any were uploaded
      if (Object.keys(imageUpdates).length > 0) {
        const { data: updatedData, error: updateError } = await supabase
          .from('bottles')
          .update(imageUpdates)
          .eq('id', data.id)
          .select('*, distilleries(id, distillery_name, country)')
          .single();

        if (updateError) throw updateError;
        return updatedData;
      }
    }

    return data;
  },

  /**
   * Updates an existing bottle
   * @param {string} id - Bottle UUID
   * @param {Object} updates - Fields to update (may include pendingImages, removedImages, existingImages)
   * @returns {Promise<Object>} Updated bottle
   */
  async updateBottle(id, updates) {
    const { data: { user } } = await supabase.auth.getUser();

    // Extract image-related fields
    const { pendingImages, removedImages, existingImages, ...bottleData } = updates;

    const imageUpdates = {};

    // Handle image changes
    if (pendingImages || removedImages) {
      // Handle front image
      if (pendingImages?.front) {
        // Delete old image if exists, unless another bottle (e.g. a clone) still uses it
        if (existingImages?.front && !(await isImageReferencedElsewhere(existingImages.front, id))) {
          await imageService.deleteBottleImage(existingImages.front);
        }
        const frontUrl = await imageService.uploadBottleImage(pendingImages.front, id, 'front');
        imageUpdates.image_front_url = frontUrl;
      } else if (removedImages?.front && existingImages?.front) {
        if (!(await isImageReferencedElsewhere(existingImages.front, id))) {
          await imageService.deleteBottleImage(existingImages.front);
        }
        imageUpdates.image_front_url = null;
      }

      // Handle back image
      if (pendingImages?.back) {
        // Delete old image if exists, unless another bottle (e.g. a clone) still uses it
        if (existingImages?.back && !(await isImageReferencedElsewhere(existingImages.back, id))) {
          await imageService.deleteBottleImage(existingImages.back);
        }
        const backUrl = await imageService.uploadBottleImage(pendingImages.back, id, 'back');
        imageUpdates.image_back_url = backUrl;
      } else if (removedImages?.back && existingImages?.back) {
        if (!(await isImageReferencedElsewhere(existingImages.back, id))) {
          await imageService.deleteBottleImage(existingImages.back);
        }
        imageUpdates.image_back_url = null;
      }
    }

    const { data, error } = await supabase
      .from('bottles')
      .update({
        ...bottleData,
        ...imageUpdates,
        updated_by: user.id
      })
      .eq('id', id)
      .select('*, distilleries(id, distillery_name, country)')
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Fetches bottles for a specific user (shared view, read-only).
   * The caller must have been granted access via collection_shares (RLS enforced).
   * @param {string} userId - Owner's UUID
   * @param {Object} options - Same options as getBottles
   * @returns {Promise<{data: Array, count: number}>}
   */
  async getBottlesForUser(userId, { page = 1, limit = 20, sortBy = 'created_at', sortOrder = 'desc', filters = {} } = {}) {
    let query = supabase
      .from('bottles')
      .select('*, distilleries(id, distillery_name, country)', { count: 'exact' })
      .eq('user_id', userId);

    if (sortBy === 'purchase_date') {
      const isAscending = sortOrder === 'asc';
      query = query
        .order('purchase_date', { ascending: isAscending, nullsFirst: isAscending })
        .order('bottle_name', { ascending: true });
    } else {
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });
    }

    if (filters.search) {
      query = query.or(`bottle_name.ilike.%${filters.search}%,notes.ilike.%${filters.search}%`);
    }
    if (filters.type) {
      query = query.eq('type', filters.type);
    }
    if (filters.status) {
      if (Array.isArray(filters.status) && filters.status.length > 0) {
        query = query.in('status', filters.status);
      } else if (typeof filters.status === 'string') {
        query = query.eq('status', filters.status);
      }
    }
    if (filters.size) {
      query = query.eq('size', filters.size);
    }

    if (limit !== 'All') {
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);
    }

    const { data, error, count } = await query;
    if (error) throw error;
    return { data: data || [], count: count || 0 };
  },

  /**
   * Deletes a bottle
   * @param {string} id - Bottle UUID
   * @returns {Promise<void>}
   */
  async deleteBottle(id) {
    const { error } = await supabase
      .from('bottles')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};
